import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * "Stampa il tuo PDF" configurator (sections/md-pdf-print.liquid).
 *
 * Price logic: the per-page price is the Shopify variant price (data-price, in cents). Each PDF
 * becomes one cart line with quantity = pages × copies, so Shopify computes the real total and
 * the checkout charges exactly what the configurator shows. The PDF itself is posted as a
 * private line item property: Shopify uploads it to its CDN and stores the URL on the line.
 *
 * "Invia ordine su WhatsApp" reuses that same upload (#postPdf) only to obtain the CDN URL of the
 * file, then removes the temporary line so the cart is left as it was. The URL is cached per file,
 * so a second send (e.g. after changing copies) does not upload again.
 *
 * @typedef {{ id: number, file: File, name: string, status: 'loading' | 'ready' | 'error', pages: number, error?: string, url?: string }} PdfItem
 * @typedef {object} Refs
 * @property {HTMLInputElement} input
 * @property {HTMLLabelElement} dropzone
 * @property {HTMLUListElement} fileList
 * @property {HTMLElement} error
 * @property {HTMLInputElement} copies
 * @property {HTMLButtonElement} minus
 * @property {HTMLButtonElement} plus
 * @property {HTMLElement} summary
 * @property {HTMLElement} total
 * @property {HTMLButtonElement} add
 * @property {HTMLElement} addLabel
 * @property {HTMLButtonElement} [waOrder] - extra WhatsApp button, rendered only in cart mode
 * @property {HTMLElement} progress
 * @property {HTMLElement} progressBar
 * @property {HTMLElement} status
 * @property {HTMLScriptElement} strings
 * @property {HTMLElement} [bar] - compact total bar for phones
 * @property {HTMLElement} [barTotal]
 * @property {HTMLButtonElement} [barGo]
 * @extends {Component<Refs>}
 */
class MdPdfPrint extends Component {
  requiredRefs = ['input', 'dropzone', 'fileList', 'copies', 'total', 'add', 'summary', 'strings'];

  /** @type {PdfItem[]} */
  #items = [];
  #nextId = 1;
  #busy = false;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Promise<any> | null} */
  #pdfjs = null;
  /** @type {Record<string, string>} */
  #t = {};
  /** @type {number | null} */
  #replaceId = null;
  /** @type {string | null} */
  #emptySummary = null;
  #listSignature = '';
  #totalCents = 0;
  #summaryInView = false;
  /** @type {IntersectionObserver | null} */
  #summaryObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    try {
      this.#t = JSON.parse(this.refs.strings.textContent || '{}');
    } catch {
      this.#t = {};
    }

    this.refs.input.addEventListener('change', this.#onInput, { signal });
    const zone = this.refs.dropzone;
    for (const type of ['dragenter', 'dragover']) {
      zone.addEventListener(type, (event) => {
        event.preventDefault();
        zone.classList.add('is-dragover');
      }, { signal });
    }
    for (const type of ['dragleave', 'dragend']) {
      zone.addEventListener(type, () => zone.classList.remove('is-dragover'), { signal });
    }
    zone.addEventListener('drop', (event) => {
      event.preventDefault();
      zone.classList.remove('is-dragover');
      if (event.dataTransfer?.files?.length) this.#addFiles([...event.dataTransfer.files]);
    }, { signal });

    this.refs.minus.addEventListener('click', () => this.#stepCopies(-1), { signal });
    this.refs.plus.addEventListener('click', () => this.#stepCopies(1), { signal });
    this.refs.copies.addEventListener('input', () => this.#render(), { signal });
    this.refs.copies.addEventListener('change', () => this.#normalizeCopies(), { signal });
    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'radio') this.#render();
    }, { signal });
    this.refs.fileList.addEventListener('click', this.#onFileAction, { signal });
    this.refs.add.addEventListener('click', this.#onAdd, { signal });
    this.refs.waOrder?.addEventListener('click', this.#onWhatsAppOrder, { signal });
    this.refs.barGo?.addEventListener('click', this.#goToSummary, { signal });
    this.#summaryObserver?.disconnect();
    if (this.refs.bar) {
      this.#summaryObserver = new IntersectionObserver(([entry]) => {
        this.#summaryInView = Boolean(entry?.isIntersecting);
        this.#updateBar();
      });
      this.#summaryObserver.observe(this.refs.summary.closest('aside') || this.refs.summary);
    }
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#summaryObserver?.disconnect();
    this.#summaryObserver = null;
  }

  /* ---------- phone total bar ---------- */

  #updateBar() {
    const { bar, barTotal } = this.refs;
    if (!bar) return;
    if (barTotal) barTotal.textContent = this.refs.total.textContent;
    bar.hidden = !(this.#totalCents > 0) || this.#summaryInView;
  }

  #goToSummary = () => {
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    (this.refs.summary.closest('aside') || this.refs.summary).scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
    // Land on the action the visitor came for; the summary text is right above it.
    if (!this.refs.add.disabled) this.refs.add.focus({ preventScroll: true });
  };

  /* ---------- config ---------- */

  get #maxBytes() {
    return Number(this.dataset.maxBytes) || 20 * 1024 * 1024;
  }

  get #maxFiles() {
    return Number(this.dataset.maxFiles) || 1;
  }

  get #maxCopies() {
    return Number(this.refs.copies.max) || 100;
  }

  /** @returns {HTMLInputElement | null} */
  get #selectedType() {
    return this.querySelector('.md-pdf__type input:checked');
  }

  /* ---------- files ---------- */

  #onInput = () => {
    const files = [...(this.refs.input.files || [])];
    this.refs.input.value = '';
    if (!files.length) return;
    if (this.#replaceId !== null) {
      const index = this.#items.findIndex((item) => item.id === this.#replaceId);
      this.#replaceId = null;
      this.refs.input.multiple = this.#maxFiles > 1;
      if (index > -1) this.#items.splice(index, 1);
      this.#addFiles(files.slice(0, 1), index > -1 ? index : undefined);
      return;
    }
    this.#addFiles(files);
  };

  /**
   * @param {File[]} files
   * @param {number} [at]
   */
  #addFiles(files, at) {
    this.#setError('');
    const room = this.#maxFiles - this.#items.length;
    if (files.length > room) {
      this.#setError(this.#t.err_count || '');
      files = files.slice(0, Math.max(room, 0));
    }
    for (const file of files) {
      /** @type {PdfItem} */
      const item = { id: this.#nextId++, file, name: sanitizeName(file.name), status: 'loading', pages: 0 };
      if (at === undefined) this.#items.push(item);
      else this.#items.splice(at++, 0, item);
      this.#analyze(item);
    }
    this.#render();
  }

  /** @param {PdfItem} item */
  async #analyze(item) {
    const fail = (/** @type {string} */ message) => {
      item.status = 'error';
      item.error = message;
      this.#render();
    };
    if (item.file.size > this.#maxBytes) return fail(this.#t.err_size || '');
    const looksPdf = item.file.type === 'application/pdf' || /\.pdf$/i.test(item.file.name);
    if (!looksPdf) return fail(this.#t.err_type || '');

    let data;
    try {
      data = new Uint8Array(await item.file.arrayBuffer());
    } catch {
      return fail(this.#t.err_corrupt || '');
    }
    // Do not trust the extension: a real PDF starts with "%PDF-" (allow a short preamble)
    const head = new TextDecoder('latin1').decode(data.subarray(0, 1024));
    if (!head.includes('%PDF-')) return fail(this.#t.err_type || '');

    let pdfjs;
    try {
      pdfjs = await this.#loadPdfJs();
    } catch {
      return fail(this.#t.err_engine || '');
    }
    try {
      const task = pdfjs.getDocument({ data, isEvalSupported: false, disableFontFace: true, stopAtErrors: true });
      const doc = await task.promise;
      const pages = doc.numPages;
      await doc.destroy();
      if (!this.#items.includes(item)) return;
      if (!Number.isInteger(pages) || pages < 1) return fail(this.#t.err_corrupt || '');
      item.pages = pages;
      item.status = 'ready';
      this.#render();
    } catch (error) {
      const name = /** @type {any} */ (error)?.name;
      fail(name === 'PasswordException' ? this.#t.err_password || '' : this.#t.err_corrupt || '');
    }
  }

  #loadPdfJs() {
    if (!this.#pdfjs) {
      this.#pdfjs = import(/* @vite-ignore */ this.dataset.pdfjs || '').then((module) => {
        module.GlobalWorkerOptions.workerSrc = this.dataset.pdfjsWorker;
        return module;
      });
      this.#pdfjs.catch(() => {
        this.#pdfjs = null;
      });
    }
    return this.#pdfjs;
  }

  /** @param {MouseEvent} event */
  #onFileAction = (event) => {
    const button = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-action]');
    if (!(button instanceof HTMLButtonElement) || this.#busy) return;
    const id = Number(button.dataset.id);
    if (button.dataset.action === 'remove') {
      this.#items = this.#items.filter((item) => item.id !== id);
      this.#setError('');
      this.#render();
      this.refs.input.focus();
    } else if (button.dataset.action === 'replace') {
      this.#replaceId = id;
      this.refs.input.multiple = false;
      this.refs.input.click();
    }
  };

  /* ---------- copies ---------- */

  #copies() {
    const value = Math.floor(Number(this.refs.copies.value));
    if (!Number.isFinite(value) || value < 1) return 1;
    return Math.min(value, this.#maxCopies);
  }

  #normalizeCopies() {
    this.refs.copies.value = String(this.#copies());
    this.#render();
  }

  /** @param {number} delta */
  #stepCopies(delta) {
    this.refs.copies.value = String(Math.min(Math.max(this.#copies() + delta, 1), this.#maxCopies));
    this.#render();
  }

  /* ---------- render ---------- */

  #render() {
    const t = this.#t;
    const list = this.refs.fileList;
    list.hidden = this.#items.length === 0;
    // Rebuild the list only when the files change: re-rendering on every price update would
    // replace the Sostituisci/Rimuovi buttons under the pointer and swallow the click.
    const signature = this.#items.map((item) => `${item.id}:${item.status}:${item.pages}:${item.error || ''}`).join('|');
    if (signature !== this.#listSignature) {
      this.#listSignature = signature;
      list.replaceChildren(
      ...this.#items.map((item) => {
        const li = document.createElement('li');
        li.className = 'md-pdf__file' + (item.status === 'error' ? ' is-error' : '');
        const icon = document.createElement('span');
        icon.className = 'md-pdf__file-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = this.refs.dropzone.querySelector('.md-pdf__drop-icon')?.innerHTML || '';
        const name = document.createElement('span');
        name.className = 'md-pdf__file-name';
        name.textContent = item.name;
        const meta = document.createElement('span');
        meta.className = 'md-pdf__file-meta';
        const size = formatSize(item.file.size, this.dataset.locale);
        if (item.status === 'loading') {
          meta.innerHTML = `<span class="md-pdf__spinner" aria-hidden="true"></span>`;
          meta.append(`${t.analyzing || ''} · ${size}`);
        } else if (item.status === 'error') {
          meta.textContent = `${item.error || ''} · ${size}`;
        } else {
          const strong = document.createElement('strong');
          strong.textContent = plural(item.pages, t.pages_one, t.pages_other);
          meta.append(strong, ` · ${size}`);
        }
        const actions = document.createElement('span');
        actions.className = 'md-pdf__file-actions';
        for (const [action, label] of [['replace', t.replace], ['remove', t.remove]]) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'md-pdf__file-btn';
          button.dataset.action = action;
          button.dataset.id = String(item.id);
          button.textContent = label || action;
          button.setAttribute('aria-label', `${label}: ${item.name}`);
          actions.append(button);
        }
        li.append(icon, name, meta, actions);
        return li;
      })
      );
    }

    const copies = this.#copies();
    this.refs.minus.disabled = copies <= 1;
    this.refs.plus.disabled = copies >= this.#maxCopies;

    const type = this.#selectedType;
    const unit = Number(type?.dataset.price || 0);
    const ready = this.#items.filter((item) => item.status === 'ready');
    const pending = this.#items.some((item) => item.status === 'loading');
    const pages = ready.reduce((sum, item) => sum + item.pages, 0);
    const totalCents = pages * copies * unit;

    this.refs.total.textContent = formatMoney(totalCents, this.dataset.locale, this.dataset.currency);
    this.#totalCents = totalCents;
    this.#updateBar();
    this.#emptySummary ??= this.refs.summary.textContent || '';
    if (!(pages > 0 && type)) this.refs.summary.textContent = this.#emptySummary;
    if (pages > 0 && type) {
      const pagesLabel = plural(pages, t.pages_one, t.pages_other);
      const copiesLabel = plural(copies, t.copies_one, t.copies_other);
      const files = ready.length > 1 ? ` (${plural(ready.length, '', t.files_other)})` : '';
      this.refs.summary.textContent = (t.summary || '[pages] × [copies] · [type]')
        .replace('[pages]', pagesLabel + files)
        .replace('[copies]', copiesLabel)
        .replace('[type]', type.dataset.title || '');
    }
    // Purchase only when every selected file is analysed and valid
    const allValid = this.#items.length > 0 && !pending && this.#items.every((item) => item.status === 'ready');
    this.refs.add.disabled = this.#busy || !allValid || !type || (this.dataset.mode === 'whatsapp' && !(totalCents > 0));
    if (this.refs.waOrder) this.refs.waOrder.disabled = this.#busy || !allValid || !type || !(totalCents > 0);
  }

  /** @param {string} message */
  #setError(message) {
    if (!this.refs.error) return;
    this.refs.error.textContent = message;
    this.refs.error.hidden = !message;
  }

  /* ---------- upload (shared by cart and WhatsApp) ---------- */

  /**
   * Adds one PDF as a cart line: the file travels as the private `_PDF` property and Shopify
   * returns the line with the CDN URL of the stored file.
   * @param {HTMLInputElement} type
   * @param {PdfItem} item
   * @param {number} copies
   * @param {(loaded: number) => void} onProgress
   * @param {string[]} [sectionIds]
   */
  #postPdf(type, item, copies, onProgress, sectionIds = []) {
    const t = this.#t;
    const form = new FormData();
    form.append('id', type.value);
    form.append('quantity', String(item.pages * copies));
    form.append(`properties[${t.prop_name}]`, item.name);
    form.append(`properties[${t.prop_pages}]`, String(item.pages));
    form.append(`properties[${t.prop_copies}]`, String(copies));
    form.append(`properties[_${t.prop_type}]`, type.dataset.title || '');
    // Unique per file: two PDFs with the same variant must never merge into one cart line
    form.append('properties[_job_id]', jobId());
    form.append('properties[_PDF]', item.file, item.name);
    if (sectionIds.length) form.append('sections', sectionIds.join(','));
    return upload(form, onProgress);
  }

  /* ---------- order via WhatsApp (no online payment) ---------- */

  #onWhatsAppOrder = async () => {
    const type = this.#selectedType;
    const items = this.#items.filter((item) => item.status === 'ready');
    if (!type || !items.length || this.#busy || items.length !== this.#items.length) return;
    const t = this.#t;
    const copies = this.#copies();
    const unit = Number(type.dataset.price || 0);
    if (!(items.reduce((sum, item) => sum + item.pages, 0) * copies * unit > 0)) return;

    // Desktop: open the WhatsApp Web tab now, inside the click, so popup blockers allow it;
    // it is pointed at the chat once the upload is done. Mobile: same-tab link opens the app.
    const mobile = isMobile();
    const popup = mobile ? null : window.open('', '_blank');
    if (popup) popup.opener = null;

    this.#busy = true;
    this.#setError('');
    this.refs.status.textContent = t.wa_preparing || '';
    this.refs.progress.hidden = false;
    this.#render();
    try {
      const missing = items.filter((item) => !item.url);
      const totalBytes = missing.reduce((sum, item) => sum + item.file.size, 0) || 1;
      let doneBytes = 0;
      for (const item of missing) {
        const line = await this.#postPdf(type, item, copies, (loaded) => {
          this.refs.progressBar.style.width = `${Math.round(((doneBytes + loaded) / totalBytes) * 100)}%`;
        });
        const url = line?.properties?._PDF;
        // Only the URL is needed: take the temporary line out so the cart stays unchanged
        if (line?.key) await removeLine(line.key);
        if (typeof url !== 'string' || !/^https:\/\//.test(url)) throw new Error('Missing PDF URL');
        item.url = url;
        doneBytes += item.file.size;
      }

      const text = this.#whatsAppText(type, items, copies);
      const phone = this.dataset.whatsapp || '';
      const target = mobile
        ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
        : `https://web.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(text)}`;
      if (popup && !popup.closed) popup.location.href = target;
      else if (mobile) window.location.href = target;
      else window.open(target, '_blank', 'noopener') || (window.location.href = target);
      this.refs.status.textContent = t.wa_ready || '';
    } catch {
      popup?.close();
      this.refs.status.textContent = '';
      this.#setError(t.wa_err || '');
    } finally {
      this.#busy = false;
      this.refs.progress.hidden = true;
      this.refs.progressBar.style.width = '0';
      this.#render();
    }
  };

  /**
   * One block with the details for a single PDF; a numbered section per file plus the order
   * total when there are more.
   * @param {HTMLInputElement} type
   * @param {PdfItem[]} items
   * @param {number} copies
   */
  #whatsAppText(type, items, copies) {
    const t = this.#t;
    const unit = Number(type.dataset.price || 0);
    const money = (/** @type {number} */ cents) => formatMoney(cents, this.dataset.locale, this.dataset.currency);
    const title = type.dataset.title || '';
    const fill = (/** @type {string | undefined} */ template, /** @type {Record<string, string>} */ values) =>
      Object.entries(values).reduce((text, [key, value]) => text.replace(`[${key}]`, value), template || '');

    if (items.length === 1) {
      const [item] = items;
      return [
        t.wa_hello,
        '',
        fill(t.wa_line_file, { name: item.name }),
        fill(t.wa_line_pages, { n: String(item.pages) }),
        fill(t.wa_line_copies, { n: String(copies) }),
        fill(t.wa_line_type, { type: title }),
        fill(t.wa_line_total, { total: money(item.pages * copies * unit) }),
        '',
        t.wa_line_pdf,
        item.url,
      ].join('\n');
    }

    const lines = [t.wa_hello || '', ''];
    for (const [index, item] of items.entries()) {
      lines.push(
        fill(t.wa_file_heading, { n: String(index + 1) }),
        item.name,
        plural(item.pages, t.pages_one, t.pages_other),
        plural(copies, t.copies_one, t.copies_other),
        title,
        money(item.pages * copies * unit),
        `${t.wa_line_pdf} ${item.url}`,
        ''
      );
    }
    const pages = items.reduce((sum, item) => sum + item.pages, 0);
    lines.push(fill(t.wa_order_total, { total: money(pages * copies * unit) }));
    return lines.join('\n');
  }

  /* ---------- add to cart ---------- */

  #onAdd = async () => {
    if (this.dataset.mode === 'whatsapp') return this.#onWhatsAppOrder();
    const type = this.#selectedType;
    const items = this.#items.filter((item) => item.status === 'ready');
    if (!type || !items.length || this.#busy) return;
    const t = this.#t;
    const copies = this.#copies();
    this.#busy = true;
    this.#setError('');
    this.refs.status.textContent = '';
    this.refs.progress.hidden = false;
    this.refs.addLabel.textContent = t.uploading || '';
    this.#render();

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: type.value, quantity: item.pages * copies })),
        promise: deferred.promise,
      })
    );

    let sections;
    try {
      const totalBytes = items.reduce((sum, item) => sum + item.file.size, 0) || 1;
      let doneBytes = 0;
      for (const [index, item] of items.entries()) {
        const response = await this.#postPdf(
          type,
          item,
          copies,
          (loaded) => {
            this.refs.progressBar.style.width = `${Math.round(((doneBytes + loaded) / totalBytes) * 100)}%`;
          },
          index === items.length - 1 ? sectionIds : []
        );
        if (response.status && response.status !== 200) throw new Error(response.description || response.message);
        doneBytes += item.file.size;
        sections = response.sections || sections;
      }
      const cart = await (await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'md-pdf-print', sourceId: this.dataset.sectionId, itemCount: cart.item_count, sections, didError: false },
      });
      this.#items = [];
      this.refs.status.textContent = t.added || '';
    } catch (error) {
      deferred.reject(error);
      this.#setError(t.err_add || '');
      this.dispatchEvent(new CartErrorEvent({ error: String(/** @type {any} */ (error)?.message || 'PDF add failed'), code: 'INVALID' }));
    } finally {
      this.#busy = false;
      this.refs.progress.hidden = true;
      this.refs.progressBar.style.width = '0';
      this.refs.addLabel.textContent = t.add || '';
      this.#render();
    }
  };
}

/**
 * Multipart POST to /cart/add.js with upload progress.
 * @param {FormData} form
 * @param {(loaded: number) => void} onProgress
 * @returns {Promise<any>}
 */
function upload(form, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', window.Theme?.routes?.cart_add_url || '/cart/add.js');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.upload.onprogress = (event) => event.lengthComputable && onProgress(event.loaded);
    xhr.onload = () => {
      let body = {};
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        return reject(new Error(`HTTP ${xhr.status}`));
      }
      if (xhr.status >= 400) return reject(new Error(/** @type {any} */ (body).description || `HTTP ${xhr.status}`));
      resolve(body);
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(form);
  });
}

/**
 * Removes a cart line by key (used after the WhatsApp upload, which only needs the file URL).
 * @param {string} key
 */
async function removeLine(key) {
  const response = await fetch(`${window.Theme?.routes?.cart_change_url || '/cart/change'}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity: 0 }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
}

/** Phones and tablets open the WhatsApp app; desktops go to WhatsApp Web. */
function isMobile() {
  const data = /** @type {any} */ (navigator).userAgentData;
  if (typeof data?.mobile === 'boolean' && data.mobile) return true;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));
}

/** Random, non-guessable job id (UUID v4 when available). */
function jobId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Keeps a readable, safe file name: no path, no control or markup characters, max 100 chars.
 * @param {string} name
 */
function sanitizeName(name) {
  const base = String(name || 'documento.pdf').split(/[\\/]/).pop() || 'documento.pdf';
  const clean = base.replace(/[\u0000-\u001f\u007f<>:"|?*`$[\]{}]/g, '').replace(/\s+/g, ' ').trim();
  const trimmed = clean.length > 100 ? clean.slice(0, 96) + '.pdf' : clean;
  return trimmed || 'documento.pdf';
}

/**
 * @param {number} count
 * @param {string} [one]
 * @param {string} [other]
 */
function plural(count, one, other) {
  return (count === 1 ? one || other || '' : other || '').replace('[n]', String(count));
}

/**
 * @param {number} bytes
 * @param {string} [locale]
 */
function formatSize(bytes, locale) {
  const mb = bytes / 1048576;
  const value = mb >= 1 ? mb : bytes / 1024;
  return `${new Intl.NumberFormat(locale || 'it', { maximumFractionDigits: 1 }).format(value)} ${mb >= 1 ? 'MB' : 'KB'}`;
}

/**
 * @param {number} cents
 * @param {string} [locale]
 * @param {string} [currency]
 */
function formatMoney(cents, locale, currency) {
  return new Intl.NumberFormat(locale || 'it', { style: 'currency', currency: currency || 'EUR' }).format(cents / 100);
}

if (!customElements.get('md-pdf-print')) customElements.define('md-pdf-print', MdPdfPrint);
